/**
 * HTTP-cache sweep — the pure half of the service worker's once-per-
 * deployment amnesty for browser HTTP-cache entries stored under headers
 * the origin no longer sends (e.g. a year-long TTL on mutable URLs).
 *
 * The HTTP cache is not enumerable, so the deployment ships a manifest
 * (`{ id, files: [paths] }`) and the SW refreshes each listed URL with
 * cache:'no-cache': revalidation sends If-None-Match, and a 304 REWRITES
 * the stored entry's headers (RFC 9111 freshening) — the poisoned TTL is
 * replaced without re-transferring unchanged bytes. Any full response
 * (including a 404 for a file the new deployment moved) equally replaces
 * the stale entry. Runs once per manifest id: the marker survives in its
 * own cache bucket, which the activate cleanup deliberately leaves alone.
 * Network failures leave the marker unset so the sweep retries on a later
 * SW start.
 *
 * Where the manifest path comes from matters more than it looks
 * (alert.live findings item 27, 2026-09-15): the SW's `config` is a static
 * import of config.js, served through the HTTP cache under the
 * registration's default `updateViaCache: 'imports'`. A config.js stored
 * under a poisoned TTL is itself among the entries the sweep must
 * refresh — so an enablement read from config can never reach the
 * profiles that need it. sw.js is the one file whose update check
 * bypasses the HTTP cache, so the default lives there: `resolveManifestUrl`
 * falls back to `sweep-manifest.json` beside sw.js. `config` may still
 * name an explicit path, or `false` to opt out.
 *
 * On a deployment that ships no manifest the default fetch 404s (or an SPA
 * fallback answers 200 with HTML) and the sweep is a silent no-op before
 * any cache bucket is opened — one small request per SW process start,
 * the same cost a manifest-bearing deployment already pays for its id
 * check. Only an *explicitly* configured path that fails is worth a
 * warning.
 */

export const SWEEP_CACHENAME = 'http-sweep'
export const SWEEP_MARKER_URL = '/__sweep-marker'
export const SWEEP_DEFAULT_FILENAME = 'sweep-manifest.json'

/**
 * @param {unknown} configured  config.cacheSweepManifest — string path,
 *   `false` to opt out, anything else → the convention
 * @param {string} swHref  self.location.href — the SW script's own URL
 * @returns {{ url: string, explicit: boolean } | null}
 */
export function resolveManifestUrl(configured, swHref) {
    if (configured === false) return null
    if (typeof configured === 'string' && configured)
        return { url: configured, explicit: true }
    return { url: new URL(SWEEP_DEFAULT_FILENAME, swHref).href, explicit: false }
}

/**
 * @param {object} deps
 * @param {(url: string, init?: object) => Promise<Response>} deps.fetch
 * @param {CacheStorage} deps.caches
 * @param {(...a) => void} [deps.log]
 * @param {(...a) => void} [deps.warn]
 * @param {number} [deps.concurrency]
 */
export function createCacheSweeper({
    fetch,
    caches,
    log = () => {},
    warn = () => {},
    concurrency = 8,
}) {
    let checkedThisRun = false

    /**
     * Once per SW process: fetch the manifest, compare its id with the
     * marker, refresh every listed path on a change. Resolves to one of
     * 'disabled' | 'no-manifest' | 'already-swept' | 'swept' |
     * 'incomplete' | 'skipped' — for tests and logs; callers ignore it.
     */
    async function maybeRun(resolved) {
        if (checkedThisRun) return 'already-checked'
        if (!resolved) return 'disabled'
        checkedThisRun = true
        const { url, explicit } = resolved
        try {
            const res = await fetch(url, { cache: 'no-cache' })
            if (!res.ok) return 'no-manifest'
            let manifest
            try {
                manifest = await res.json()
            } catch (e) {
                // An SPA fallback answering 200 HTML for a missing file is
                // the no-manifest case in disguise; only an explicit path
                // deserves the warning.
                if (explicit) warn(`[sweep] ${url} is not JSON:`, e?.message ?? e)
                return 'no-manifest'
            }
            if (!manifest?.id || !Array.isArray(manifest.files)) {
                if (explicit) warn(`[sweep] ${url} has no { id, files }`)
                return 'no-manifest'
            }

            const marker = await caches.open(SWEEP_CACHENAME)
            const prev = await marker.match(SWEEP_MARKER_URL)
            if (prev && (await prev.text()) === manifest.id) return 'already-swept'

            log(`[sweep] deployment ${manifest.id}: refreshing ${manifest.files.length} HTTP-cache entries`)
            const queue = [...manifest.files]
            let failures = 0
            await Promise.all(
                Array.from({ length: concurrency }, async () => {
                    while (queue.length) {
                        const path = queue.shift()
                        try {
                            await fetch(path, { cache: 'no-cache', credentials: 'same-origin' })
                        } catch {
                            failures++ // offline mid-sweep — retry next SW start
                        }
                    }
                })
            )
            if (failures === 0) {
                await marker.put(SWEEP_MARKER_URL, new Response(manifest.id))
                log(`[sweep] complete — marked ${manifest.id}`)
                return 'swept'
            }
            warn(`[sweep] ${failures} fetches failed — will retry on a later SW start`)
            return 'incomplete'
        } catch (err) {
            warn('[sweep] skipped:', err?.message || err)
            return 'skipped'
        }
    }

    return { maybeRun }
}
